/* eslint-disable @typescript-eslint/camelcase */
import { all, put, takeLatest } from 'redux-saga/effects';
import { messagesRequest } from 'modules/messages/actions';
import i18n from 'i18n';
import endpoints, { api } from './endpoints';
import {
  getUsersForRecoveryFulfilled,
  getUsersForRecoveryRejected,
  getUsersForRecoveryRequest,
  recoverUsersByRecoveryUuidsRequest,
  recoverUsersByRecoveryUuidsFulfilled,
  recoverUsersByRecoveryUuidsRejected,
  recoverUsersByRequestUuidRequest,
  recoverUsersByRequestUuidFulfilled,
  recoverUsersByRequestUuidRejected,
  getRecoveriesCountFromRequestUuidFulfilled,
  getRecoveriesCountFromRequestUuidRejected,
  getRecoveriesCountFromRequestUuidRequest,
  removeUsersFromClientsFulfilled,
  removeUsersFromClientsRejected,
  removeUsersFromClientsRequest,
  getAllUsersRejected,
  getAllUsersFulfilled,
  getAllUsersRequest,
  getUsersToManageFulfilled,
  getUsersToManageRejected,
  getUsersToManageRequest,
  removeUserFromAllClientsRejected,
  removeUserFromAllClientsFulfilled,
  removeUserFromAllClientsRequest,
  removeUserPermanentlyFulfilled,
  removeUserPermanentlyRejected,
  removeUserPermanentlyRequest,
  removeMultipleUsersPermanentlyRequest,
  removeMultipleUsersPermanentlyRejected,
  removeMultipleUsersPermanentlyFulfilled,
} from './actions';

function* getAllUsersRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getAllUsers(payload),
    });

    return yield put(getAllUsersFulfilled(data));
  } catch (error) {
    return yield put(getAllUsersRejected());
  }
}

function* getUsersForRecoveryRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getUsersForRecovery(payload),
    });

    return yield put(getUsersForRecoveryFulfilled(data));
  } catch (error) {
    return yield put(getUsersForRecoveryRejected());
  }
}

function* getRecoveriesCountFromRequestUuidRequestSaga({ payload }) {
  const { requestUuid } = payload;

  try {
    const {
      data: { count },
    } = yield api.get({
      url: endpoints.getUsersForRecovery({ request_uuid: requestUuid }),
    });

    return yield put(getRecoveriesCountFromRequestUuidFulfilled(count));
  } catch (error) {
    return yield put(getRecoveriesCountFromRequestUuidRejected());
  }
}

function* recoverUsersByRecoveryUuidsRequestSaga({ payload }) {
  const { recoveryUuids, query } = payload;
  try {
    const { data } = yield api.post({
      url: endpoints.recoverUsers(),
      data: {
        recovery_uuids: recoveryUuids,
      },
    });

    return yield all([
      put(recoverUsersByRecoveryUuidsFulfilled(data)),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('USERS_PAGE.RECOVERY_REQUESTED_KEY'),
        })
      ),
      put(getUsersForRecoveryRequest({ ...query })),
    ]);
  } catch (error) {
    return yield put(recoverUsersByRecoveryUuidsRejected());
  }
}

function* recoverUsersByRequestUuidRequestSaga({ payload }) {
  const { requestUuid, query } = payload;
  try {
    const { data } = yield api.post({
      url: endpoints.recoverUsersByRequestUuid(requestUuid),
    });

    return yield all([
      put(recoverUsersByRequestUuidFulfilled(data)),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('USERS_PAGE.RECOVERY_REQUESTED_KEY'),
        })
      ),
      put(getUsersForRecoveryRequest({ ...query })),
    ]);
  } catch (error) {
    return yield put(recoverUsersByRequestUuidRejected());
  }
}

function* removeUsersFromClientsRequestSaga({ payload }) {
  const { usersToRemove, getUsersFn } = payload;
  try {
    yield api.post({
      url: endpoints.removeUsers(),
      data: {
        users_to_remove: usersToRemove,
      },
    });

    return yield all([
      put(removeUsersFromClientsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('USERS_PAGE.DELETION_REQUESTED_KEY'),
        })
      ),
      getUsersFn(),
    ]);
  } catch (error) {
    return yield put(removeUsersFromClientsRejected());
  }
}

function* getUsersToManageRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getAllUsers(payload),
    });

    return yield put(getUsersToManageFulfilled(data));
  } catch (error) {
    return yield put(getUsersToManageRejected());
  }
}

function* removeUserFromAllClientsRequestSaga({ payload }) {
  const { userUuid, getUsersFn } = payload;
  try {
    yield api.delete({
      url: endpoints.removeUser(userUuid, false),
    });

    return yield all([
      put(removeUserFromAllClientsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('USERS_PAGE.DELETION_REQUESTED_KEY'),
        })
      ),
      getUsersFn(),
    ]);
  } catch (error) {
    return yield put(removeUserFromAllClientsRejected());
  }
}

function* removeUserPermanentlyRequestSaga({ payload }) {
  const { userUuid, getUsersFn } = payload;
  try {
    yield api.delete({
      url: endpoints.removeUser(userUuid, true),
    });

    return yield all([
      put(removeUserPermanentlyFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('USER_DELETE_PERMANENTLY_SUCCESS_MESSAGE_KEY'),
        })
      ),
      getUsersFn(),
    ]);
  } catch (error) {
    return yield put(removeUserPermanentlyRejected());
  }
}

function* removeMultipleUsersPermanentlyRequestSaga({ payload }) {
  const { usersToRemove, getUsersFn } = payload;

  try {
    yield api.post({
      url: endpoints.removeUsersPermanently(),
      data: { users_uuid_to_remove: Array.from(usersToRemove) },
    });

    return yield all([
      put(removeMultipleUsersPermanentlyFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('USERS_PAGE.DELETION_REQUESTED_KEY'),
        })
      ),
      getUsersFn(),
    ]);
  } catch (error) {
    return yield put(removeMultipleUsersPermanentlyRejected());
  }
}

export default all([
  takeLatest(getAllUsersRequest, getAllUsersRequestSaga),
  takeLatest(getUsersForRecoveryRequest, getUsersForRecoveryRequestSaga),
  takeLatest(
    getRecoveriesCountFromRequestUuidRequest,
    getRecoveriesCountFromRequestUuidRequestSaga
  ),
  takeLatest(
    recoverUsersByRecoveryUuidsRequest,
    recoverUsersByRecoveryUuidsRequestSaga
  ),
  takeLatest(
    recoverUsersByRequestUuidRequest,
    recoverUsersByRequestUuidRequestSaga
  ),
  takeLatest(removeUsersFromClientsRequest, removeUsersFromClientsRequestSaga),
  takeLatest(getUsersToManageRequest, getUsersToManageRequestSaga),
  takeLatest(
    removeUserFromAllClientsRequest,
    removeUserFromAllClientsRequestSaga
  ),
  takeLatest(removeUserPermanentlyRequest, removeUserPermanentlyRequestSaga),
  takeLatest(
    removeMultipleUsersPermanentlyRequest,
    removeMultipleUsersPermanentlyRequestSaga
  ),
]);
